<template>
	<div>
		<div class="container">
			<div class="page">
				<!-- Loading State -->
				<div v-if="isLoading" class="text-center py-5">
					<div class="spinner-border text-primary" role="status">
						<span class="sr-only">Loading...</span>
					</div>
					<p class="mt-3 text-muted">Memuat data...</p>
				</div>

				<!-- Error State -->
				<div v-else-if="error" class="alert alert-danger" role="alert">
					<div class="d-flex align-items-center">
						<i class="material-icons me-2">error</i>
						<div>
							<h6 class="alert-heading mb-1">Terjadi Kesalahan</h6>
							<p class="mb-0">{{ error }}</p>
						</div>
					</div>
				</div>

				<!-- Main Content -->
				<div v-else>
					<!-- Wizard Step Indicator -->
					<WizardSteps :current-step="currentStep" />

					<div class="row">
						<!-- Main Content (Dynamic based on step) -->
						<div class="col-md-8">
							<!-- Step 1: Configuration -->
							<Step1Configuration v-if="currentStep === 1" />

							<!-- Step 2: Product Mapping -->
							<Step2ProductMapping v-if="currentStep === 2" />

							<!-- Step 3: Order Summary -->
							<Step3OrderSummary v-if="currentStep === 3" />
						</div>

						<!-- Sidebar -->
						<div class="col-md-4">
							<SidebarInfo
								:show-back-button="showBackButton"
								:can-continue="canContinue"
								:continue-button-text="continueButtonText"
								:show-grand-total="currentStep === 3"
								@back="handleBack"
								@cancel="handleCancel"
								@continue="handleContinue"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Loading Modal -->
		<LoadingAddProduct
			:isVisible="showLoadingModal"
			:isLoading="isSubmitting"
			@close="closeLoadingModal"
		/>

		<!-- Process Import Order Modal -->
		<ProcessImportOrder
			v-if="showProcessModal"
			:summary-items="summaryItems"
			:selected-products="selectedProducts"
			:supplier-id="selectedSupplierId"
			:mapping-data-per-package="mappingDataPerPackage"
			:mapping-data-per-product="mappingDataPerProduct"
			:shipping-label-file="shippingLabelFile"
			:excel-file="selectedFile"
			@close="handleProcessModalClose"
		/>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import WizardSteps from '@/components/import-wholesale-order/WizardSteps.vue';
import Step1Configuration from '@/components/import-wholesale-order/Step1Configuration.vue';
import Step2ProductMapping from '@/components/import-wholesale-order/Step2ProductMapping.vue';
import Step3OrderSummary from '@/components/import-wholesale-order/Step3OrderSummary.vue';
import SidebarInfo from '@/components/import-wholesale-order/SidebarInfo.vue';
import LoadingAddProduct from '@/components/marketplace/LoadingAddProduct.vue';
import ProcessImportOrder from '@/components/import-wholesale-order/modal/ProcessImportOrder.vue';

export default {
	name: 'ImportWholesaleOrderPage',
	components: {
		WizardSteps,
		Step1Configuration,
		Step2ProductMapping,
		Step3OrderSummary,
		SidebarInfo,
		LoadingAddProduct,
		ProcessImportOrder,
	},
	data() {
		return {
			isLoading: false,
			showLoadingModal: false,
			showProcessModal: false,
		};
	},
	computed: {
		...mapGetters('importWholesaleOrder', [
			'currentStep',
			'totalSteps',
			'error',
			'isProcessing',
			'isSubmitting',
			'canContinueStep1',
			'canContinueStep2',
			'canContinueStep3',
			'selectedSupplier',
			'selectedSupplierId',
			'summaryItems',
			'selectedProducts',
			'suppliers',
			'mappingDataPerPackage',
			'mappingDataPerProduct',
			'shippingLabelFile',
			'selectedFile',
		]),
		showBackButton() {
			return this.currentStep > 1;
		},
		continueButtonText() {
			if (this.currentStep === 1) return 'Lanjut ke Mapping';
			if (this.currentStep === 2) return 'Lanjut ke Ringkasan';
			return 'Submit Order';
		},
		canContinue() {
			if (this.currentStep === 1) return this.canContinueStep1;
			if (this.currentStep === 2) return this.canContinueStep2;
			if (this.currentStep === 3) return this.canContinueStep3;
			return false;
		},
	},
	async created() {
		await this.initPage();
	},
	methods: {
		...mapActions('importWholesaleOrder', [
			'nextStep',
			'prevStep',
			'prepareSummary',
			'setIsSubmitting',
			'setError',
			'resetState',
			'loadAvailableProducts',
			'autoMatchProducts',
		]),

		async initPage() {
			this.isLoading = true;
			try {
				// Reset state when entering the page
				this.resetState();
			} catch (error) {
				console.error('Error initializing page:', error);
				this.setError('Gagal memuat data. Silakan coba lagi.');
			} finally {
				this.isLoading = false;
			}
		},

		async handleContinue() {
			if (this.currentStep === 1) {
				if (this.canContinue) {
					// Load products for selected supplier before moving to step 2
					if (this.selectedSupplier && this.selectedSupplier.code) {
						this.isLoading = true;
						try {
							const products = await this.loadAvailableProducts({
								supplierCode: this.selectedSupplier.code,
							});

							if (products.length > 0) {
								// Auto-match products after loading
								const matchResult = this.autoMatchProducts();

								if (matchResult.matched > 0) {
									this.$notify({
										group: 'app',
										title: 'Berhasil',
										text: `Produk dari ${this.selectedSupplier.code} berhasil dimuat. ${matchResult.matched} dari ${matchResult.total} produk berhasil dimapping otomatis.`,
										type: 'success',
									});
								} else {
									this.$notify({
										group: 'app',
										title: 'Berhasil',
										text: `Produk dari ${this.selectedSupplier.code} berhasil dimuat. Silakan mapping produk secara manual.`,
										type: 'success',
									});
								}
							} else {
								this.$notify({
									group: 'app',
									title: 'Info',
									text: `Tidak ada produk untuk supplier ${this.selectedSupplier.code}`,
									type: 'info',
								});
							}
						} catch (error) {
							console.error('Error loading products:', error);
							this.$notify({
								group: 'app',
								title: 'Error',
								text: 'Gagal memuat daftar produk',
								type: 'error',
							});
							return;
						} finally {
							this.isLoading = false;
						}
					}

					await this.nextStep();
				}
			} else if (this.currentStep === 2) {
				if (this.canContinue) {
					// Prepare summary before moving to step 3
					await this.prepareSummary();
					await this.nextStep();
				}
			} else {
				await this.submitOrder();
			}
		},

		handleBack() {
			this.prevStep();
		},

		handleCancel() {
			// eslint-disable-next-line no-alert, no-restricted-globals
			const confirmed = confirm('Apakah Anda yakin ingin membatalkan import? Data yang belum disubmit akan hilang.');
			if (confirmed) {
				this.$router.push('/wholesale-order/list');
			}
		},

		submitOrder() {
			if (!this.canContinue) {
				return;
			}

			// Show process modal instead of loading modal
			this.showProcessModal = true;
		},

		handleProcessModalClose() {
			this.showProcessModal = false;
		},

		closeLoadingModal() {
			this.showLoadingModal = false;
		},
	},
};
</script>

<style scoped>
.container {
	max-width: 1500px;
	margin: 0 auto;
	padding: 2rem;
}

.page {
	padding: 1rem 0;
}
</style>
